import { TFunction } from 'next-i18next'
import { app } from '../../env'
import { AddressFieldSchema, DateOfBirthFieldSchema, FieldType, InputFieldSchema, Schema } from '@afterpaytouch/form'
import { ConsumerState } from '../../state'
import { GetSchemaAndFormDataResult, IdentityVerificationForm, SchemaUpdates } from './types'
import { nameMaxLengthValidation, namePattern, nationalIdPattern, requiredValidation } from '../VerifyIdentity/validations'
import { SupportedLocale } from '../../model'
import { Country } from '@afterpay/types'
import { requiredAddress, validatePoBox } from '../ShippingAddressForm/validations'
import { getAddressOptionalFields, isConsumerLendingEligibleState } from '../../utils/profile-validation'
import { addressHasNoPO, InputAddressData, InputAddressValidationRule, InputBirthdayDateFormatPosition } from '@afterpaytouch/core'
import { ALLOWED_ADDRESS_TYPES } from '../VerifyAddress/utils'
import { MAX_AGE_RESTRICTION, MINIMUM_AGE_RESTRICTION } from '../CreateAccount/constants'
import { LogEvent, TrackingEvent } from '../../model/monitoring'
import { validateIneligibleStates } from './validations'

const getInputFieldSchema = ({ value, label, error = false, options = {}, inline = false, show = true, otherProps = {} }): InputFieldSchema => ({
  type: FieldType.Input,
  show,
  props: {
    label,
    value,
    error,
    ...otherProps,
  },
  options,
  inline,
})

const getInputBirthdaySchema = ({ locale, label, error = false, value, options, otherValidations = {}, otherProps = {} }): DateOfBirthFieldSchema => ({
  type: FieldType.DateOfBirth,
  show: true,
  props: {
    label,
    defaultValue: value,
    locale: locale as SupportedLocale,
    error,
    hasDateFormatLabel: false,
    ...otherProps,
  },
  options,
  ...otherValidations,
})

const getAddressFieldSchema = ({ value, error = false, otherProps = {}, options = {}, disabled = false }): AddressFieldSchema => ({
  type: FieldType.Address,
  apiKey: app.GOOGLEMAPS_API_KEY,
  show: true,
  props: {
    initialAddress: value,
    error,
    disabled,
    ...otherProps,
  },
  options,
})

export const getDOBValidations = (t: TFunction, address?: InputAddressData, country?: Country): Omit<DateOfBirthFieldSchema, 'type'> => {
  const countryMinimumAgeRestriction = MINIMUM_AGE_RESTRICTION[country ?? ''] ?? MINIMUM_AGE_RESTRICTION.DEFAULT_COUNTRY
  const minAgeValue = countryMinimumAgeRestriction[address?.state ?? ''] ?? countryMinimumAgeRestriction.DEFAULT

  return {
    minAge: {
      value: minAgeValue,
      message: t('verifyIdentity:error:minAge', { context: 'dateOfBirth', minAge: minAgeValue }),
    },
    maxAge: {
      value: MAX_AGE_RESTRICTION,
      message: t('verifyIdentity:error:maxAge', { context: 'dateOfBirth', maxAge: MAX_AGE_RESTRICTION }),
    },
  }
}

export const getFormData = (consumer: ConsumerState): IdentityVerificationForm => {
  return {
    givenNames: consumer.profile?.givenNames ?? '',
    otherGivenNames: consumer.profile?.otherGivenNames ?? '',
    surname: consumer.profile?.surname ?? '',
    dateOfBirth: consumer.profile?.dateOfBirth ?? '',
    address: {
      address1: consumer.profile?.contactAddress?.address1 ?? '',
      address2: consumer.profile?.contactAddress?.address2 ?? '',
      area2: consumer.profile?.contactAddress?.area2 ?? '',
      postcode: consumer.profile?.contactAddress?.postcode ?? '',
      state: consumer.profile?.contactAddress?.state ?? '',
      suburb: consumer.profile?.contactAddress?.suburb ?? '',
      countryCode: consumer.profile?.contactAddress?.countryCode ?? '',
    },
    partialNationalIdNumberInfo: undefined,
    fullNationalIdNumber: '',
    partialNationalIdNumber: '',
    footer: undefined,
  }
}

export const getSchema = (
  consumer: ConsumerState,
  country: Country,
  components: { nationalIdNumberInfo: () => JSX.Element; footer: () => JSX.Element },
  t: TFunction,
  schemaUpdates: SchemaUpdates,
  error: boolean,
  isPOBoxError: boolean,
  setIsPoBoxError: (isPOBoxError: boolean) => void,
  logEvent: LogEvent,
  onAddressInputValueChange: () => void,
  enableFullNationalIdNumber: boolean,
  onSSNInfoClick: () => void,
  ssnIconRef: React.RefObject<HTMLButtonElement>,
  isAFF?: boolean
): GetSchemaAndFormDataResult<IdentityVerificationForm> => {
  const initialFormData = getFormData(consumer)
  const { givenNames, otherGivenNames, surname, dateOfBirth, address, partialNationalIdNumber, fullNationalIdNumber } = initialFormData
  const { accountCountry, preferredLocale } = consumer

  const required = requiredValidation(t)

  const { dobValidations } = schemaUpdates

  const namePatterns = namePattern(t)
  const nationalIdPatterns = nationalIdPattern(t)
  const nameMaxLengthOptions = nameMaxLengthValidation(t)

  const handleAddressCustomValidation = (parsedData: InputAddressData): string | null => {
    if (!addressHasNoPO(parsedData)) {
      setIsPoBoxError(true)
      return t('addNewAddress:error.invalid', { context: 'poBoxError' })
    }

    if (!isAFF && !isConsumerLendingEligibleState(parsedData)) {
      return t('consumerLending:identityVerification:formErrorLabels:ineligibleState')
    }

    setIsPoBoxError(false)
    return null
  }

  const schema: Schema<IdentityVerificationForm> = {
    givenNames: getInputFieldSchema({
      label: t('consumerLending:identityVerification:formLabels:givenNames'),
      value: givenNames,
      inline: true,
      options: {
        ...required('givenNames'),
        ...namePatterns('givenNames'),
        ...nameMaxLengthOptions('givenNames', accountCountry),
      },
      otherProps: {
        'data-dd-action-name': 'Identity Verification Consumer Lending Given Name Input',
      },
      error,
    }),
    otherGivenNames: getInputFieldSchema({
      label: t('consumerLending:identityVerification:formLabels:otherGivenNames'),
      value: otherGivenNames,
      inline: true,
      options: {
        ...namePatterns('otherGivenNames'),
        ...nameMaxLengthOptions('otherGivenNames', accountCountry),
      },
      otherProps: {
        'data-dd-action-name': 'Identity Verification Consumer Lending Other Given Name Input',
      },
      error,
    }),
    surname: getInputFieldSchema({
      label: t('consumerLending:identityVerification:formLabels:surname'),
      value: surname,
      options: {
        ...required('surname'),
        ...namePatterns('surname'),
        ...nameMaxLengthOptions('surname', accountCountry),
      },
      otherProps: {
        'data-dd-action-name': 'Identity Verification Consumer Lending Surname Input',
      },
      error,
    }),
    dateOfBirth: getInputBirthdaySchema({
      value: dateOfBirth,
      locale: preferredLocale,
      label: t('consumerLending:identityVerification:formLabels:dateOfBirth'),
      options: { ...required('dateOfBirth') },
      otherValidations: dobValidations,
      otherProps: {
        hasDateFormatLabel: true,
        dateFormatPosition: InputBirthdayDateFormatPosition.SecondaryLabel,
        'data-dd-action-name': 'Identity Verification Consumer Lending Date Of Birth Input',
      },
      error,
    }),
    address: getAddressFieldSchema({
      value: address,
      otherProps: {
        label: t('consumerLending:identityVerification:formLabels:address'),
        manualAddressLabel: t('addNewAddress:findAddressLabelCaption'),
        onManualAddressLabelClick: () => logEvent(TrackingEvent.PRESSED_MANUAL_ADDRESS_TOGGLE),
        onManualAddressChange: onAddressInputValueChange,
        onInputValueChange: onAddressInputValueChange,
        address1Label: t('addNewAddress:address1Label'),
        address2Label: t('addNewAddress:address2Label'),
        postcodeLabel: t('addNewAddress:postcodeLabel'),
        componentRestrictions: { country },
        suburbLabel: t('addNewAddress:suburbLabel'),
        stateLabel: t('addNewAddress:stateLabel'),
        manualAddressLabelCaption: t('addNewAddress:findAddressLabelCaption'),
        optionalManualAddressFields: getAddressOptionalFields(country),
        allowedAddressType: ALLOWED_ADDRESS_TYPES,
        searchAddressLabel: t('addNewAddress:searchAddressLabel'),
        defaultExpanded: false,
        validateRule: InputAddressValidationRule.ByAddress,
        onCustomValidation: handleAddressCustomValidation,
        onManualModeChange: () => setIsPoBoxError(false),
        blurFieldDependencies: ['dateOfBirth'],
        'data-dd-action-name': 'Identity Verification Consumer Lending Address Input',
      },
      options: {
        validate: {
          ...requiredAddress({ name: 'address', country, t }),
          ...validatePoBox(t('addNewAddress:error:invalid', { context: 'poBoxError' })),
          ...(isAFF ? {} : validateIneligibleStates(t('consumerLending:identityVerification:formErrorLabels:ineligibleState'))),
        },
      },
      error: error || isPOBoxError,
    }),
    partialNationalIdNumber: getInputFieldSchema({
      label: t('consumerLending:identityVerification:formLabels:partialNationalIdNumber'),
      value: partialNationalIdNumber,
      show: !enableFullNationalIdNumber,
      options: {
        maxLength: {
          value: 4,
          message: t('consumerLending:identityVerification:formErrorLabels:partialNationalIdNumberLength'),
        },
        minLength: {
          value: 4,
          message: t('consumerLending:identityVerification:formErrorLabels:partialNationalIdNumberLength'),
        },
        ...(enableFullNationalIdNumber ? {} : required('partialNationalIdNumber')),
      },
      otherProps: {
        format: '0'.repeat(4),
        inputMode: 'numeric',
        pattern: '[0-9]*',
        'data-dd-action-name': 'Identity Verification Consumer Lending Partial National ID Input',
        icon: 'Info',
        iconClickHandler: onSSNInfoClick,
        iconRef: ssnIconRef,
        'aria-describedby': 'partial-national-id-number-tooltip',
      },
      error,
    }),
    fullNationalIdNumber: getInputFieldSchema({
      label: t('consumerLending:identityVerification:formLabels:fullNationalIdNumber'),
      value: fullNationalIdNumber,
      show: enableFullNationalIdNumber,
      inline: true,
      options: {
        validate: {
          validLength: (value) => {
            const isValidLength = value.split('-').join('').length === 9
            return isValidLength || t('consumerLending:identityVerification:formErrorLabels:fullNationalIdNumberLength')
          },
        },
        ...nationalIdPatterns(),
        ...(enableFullNationalIdNumber ? required('fullNationalIdNumber') : {}),
      },
      otherProps: {
        format: '000-00-0000',
        inputMode: 'numeric',
        'data-dd-action-name': 'Identity Verification Consumer Lending Full National ID Input',
        icon: 'Info',
        iconClickHandler: onSSNInfoClick,
        iconRef: ssnIconRef,
      },
      error,
    }),
    partialNationalIdNumberInfo: {
      type: FieldType.Component,
      Component: components.nationalIdNumberInfo,
    },
    footer: {
      type: FieldType.Component,
      Component: components.footer,
    },
  }

  return { schema, initialFormData }
}
